/**
 * GTM auto-install card — rendered at top of /onboarding/website when
 * the customer's site_tech_stack === 'gtm'.
 *
 * Six sub-states (single component, derived from /api/oauth/gtm/setup-status
 * + URL params ?gtm_step + ?gtm_error):
 *   1. ready        — Connect Tag Manager primary CTA
 *   2. pick         — single-select picker of accounts+containers
 *   3. installing   — pulse + "Setting up HeyMotek in <container>…"
 *   4. succeeded    — green check + container public_id; snippet verifier
 *                     in the parent page picks up the snippet firing
 *   5. failed       — error + Retry (same account/container, idempotent)
 *                     + Reconnect (new OAuth)
 *   6. oauth_error  — friendly banner with Try again
 *
 * Design language mirrors WhatsAppConnectCard / WhatsAppConnectedCard.
 * See REFERENCE/plans/PLAN_GTM_AUTO_INSTALL.md (2026-05-29 audit section).
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tag, CheckCircle2, Loader2, ExternalLink, AlertCircle } from 'lucide-react';

import { apiRequest, ApiError } from '@/lib/api';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { useGtmSetupStatus, type GtmSetupStatus } from '@/lib/useGtmSetupStatus';

interface GtmContainer {
  account_id: string;
  account_name: string;
  container_id: string;
  container_name: string;
  public_id: string | null;
}

interface ContainersResponse {
  containers: GtmContainer[];
}

interface StartResponse {
  auth_url: string;
}

type SubState = 'ready' | 'pick' | 'installing' | 'succeeded' | 'failed' | 'oauth_error';

function getSubState(
  status: GtmSetupStatus | undefined,
  gtmStep: string | null,
  gtmError: string | null,
): SubState {
  if (gtmError) return 'oauth_error';
  if (!status) return 'ready';
  if (status.status === 'failed') return 'failed';
  if (status.status === 'running') return 'installing';
  if (status.status === 'succeeded') return 'succeeded';
  if (gtmStep === 'pick' || (status.oauth_connected && !status.account_id)) return 'pick';
  return 'ready';
}

function clearUrlParams(keys: string[]): void {
  const url = new URL(window.location.href);
  keys.forEach((k) => url.searchParams.delete(k));
  window.history.replaceState({}, '', url.toString());
}

interface GtmInstallCardProps {
  /** Customer website (from /me). Used in the success-state CTA so the user
   *  can fire a page view that triggers the snippet verifier. */
  websiteUrl?: string | null;
  /** Whether the snippet has fired/verified yet. Once true, the success view
   *  drops the "open your site" nudge and reads as plain "Setup complete". */
  snippetVerified?: boolean;
}

export function GtmInstallCard({
  websiteUrl,
  snippetVerified,
}: GtmInstallCardProps = {}) {
  const params = new URLSearchParams(window.location.search);
  const gtmStep = params.get('gtm_step');
  const gtmError = params.get('gtm_error');

  const { data: status } = useGtmSetupStatus();
  const subState = getSubState(status, gtmStep, gtmError);

  return (
    <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-50">
          <Tag className="h-7 w-7 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          {subState === 'ready' && <ReadyView />}
          {subState === 'pick' && <PickerView />}
          {subState === 'installing' && <InstallingView status={status!} />}
          {subState === 'succeeded' && <SucceededView status={status!} websiteUrl={websiteUrl ?? null} snippetVerified={!!snippetVerified} />}
          {subState === 'failed' && <FailedView status={status!} />}
          {subState === 'oauth_error' && <OAuthErrorView code={gtmError!} />}
        </div>
      </div>
    </div>
  );
}

// ---- Sub-state views ----------------------------------------------------

function ReadyView() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleConnect() {
    setBusy(true);
    setErr(null);
    try {
      const { auth_url } = await apiRequest<StartResponse>('/oauth/gtm/start');
      window.location.href = auth_url;
    } catch (e: unknown) {
      setBusy(false);
      setErr(
        (e instanceof ApiError && e.detail) ||
          "Couldn't start GTM connection. Try again.",
      );
    }
  }

  return (
    <>
      <h3 className="text-xl font-bold text-dark">Connect Google Tag Manager</h3>
      <p className="mt-2 text-base text-muted-foreground leading-relaxed">
        One-click install. We create a draft workspace, add our tag plus a trigger, and publish a version. Takes about 15 seconds.
      </p>

      <div className="mt-5 rounded-xl bg-bg-tint p-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          You authorize HeyMotek to manage tags in the container you pick. We don't touch other workspaces or other containers.
        </p>
      </div>

      <div className="mt-4 flex items-start gap-2 text-sm text-dark/80">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span>
          On the next screen, please <strong>check all four boxes</strong> — missing any one will stop the install.
        </span>
      </div>

      {err && (
        <div className="mt-4">
          <Banner variant="error" size="compact">{err}</Banner>
        </div>
      )}

      <div className="mt-5">
        <Button
          type="button"
          onClick={handleConnect}
          disabled={busy}
          className="bg-dark text-white hover:bg-dark/90 rounded-full px-6 py-3 font-bold"
        >
          {busy ? 'Opening Google…' : 'Connect Tag Manager'}
        </Button>
      </div>
    </>
  );
}

function PickerView() {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null); // "accountId:containerId"
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const { data: containersResp, isLoading, error: loadErrRaw } = useQuery({
    queryKey: ['gtm', 'containers'],
    queryFn: () => apiRequest<ContainersResponse>('/oauth/gtm/containers'),
    retry: false,
  });
  const containers = containersResp?.containers ?? null;
  const loadErr = loadErrRaw
    ? (loadErrRaw instanceof ApiError && loadErrRaw.detail) ||
      "Couldn't load your GTM containers. Try reconnecting."
    : null;

  async function handleConfirm() {
    if (!picked) return;
    const [account_id, container_id] = picked.split(':');
    setSubmitting(true);
    setSubmitErr(null);
    try {
      await apiRequest('/oauth/gtm/install', {
        method: 'POST',
        body: { account_id, container_id },
      });
      clearUrlParams(['gtm_step']);
      // Force a poll so the UI transitions to 'installing' immediately.
      await queryClient.invalidateQueries({ queryKey: ['gtm', 'setup-status'] });
    } catch (e: unknown) {
      setSubmitting(false);
      setSubmitErr(
        (e instanceof ApiError && e.detail) ||
          "Couldn't start the install. Try again.",
      );
    }
  }

  if (loadErr) {
    return (
      <>
        <h3 className="text-xl font-bold text-dark">Couldn't load containers</h3>
        <div className="mt-3">
          <Banner variant="error" size="compact">{loadErr}</Banner>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          You can use the manual install below instead.
        </p>
      </>
    );
  }

  if (isLoading || containers === null) {
    return (
      <>
        <h3 className="text-xl font-bold text-dark">Loading your containers…</h3>
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Reading your GTM accounts</span>
        </div>
      </>
    );
  }

  if (containers.length === 0) {
    return (
      <>
        <h3 className="text-xl font-bold text-dark">No web containers found</h3>
        <p className="mt-2 text-base text-muted-foreground leading-relaxed">
          Your Google account doesn't seem to have any GTM web containers we can install into. You can use the manual install below.
        </p>
      </>
    );
  }

  return (
    <>
      <h3 className="text-xl font-bold text-dark">Where should we install HeyMotek?</h3>
      <p className="mt-2 text-base text-muted-foreground leading-relaxed">
        We'll add a tag plus a trigger to a draft workspace and publish a version.
      </p>

      <div className="mt-5 space-y-2">
        {containers.map((c) => {
          const key = `${c.account_id}:${c.container_id}`;
          const selected = picked === key;
          return (
            <label
              key={key}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                selected ? 'border-brand bg-brand/5' : 'border-dark/10 hover:bg-bg-tint'
              }`}
            >
              <input
                type="radio"
                name="gtm-container"
                value={key}
                checked={selected}
                onChange={() => setPicked(key)}
                className="h-4 w-4 text-brand focus:ring-brand/30"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-dark truncate">
                  {c.account_name} → {c.container_name}
                </p>
                {c.public_id && (
                  <p className="text-xs text-muted-foreground font-mono">{c.public_id}</p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {submitErr && (
        <div className="mt-4">
          <Banner variant="error" size="compact">{submitErr}</Banner>
        </div>
      )}

      <div className="mt-5">
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={!picked || submitting}
          className="bg-dark text-white hover:bg-dark/90 rounded-full px-6 py-3 font-bold"
        >
          {submitting ? 'Starting…' : 'Confirm and install'}
        </Button>
      </div>
    </>
  );
}

function InstallingView({ status }: { status: GtmSetupStatus }) {
  return (
    <>
      <h3 className="text-xl font-bold text-dark">Setting up HeyMotek in your Tag Manager…</h3>
      <p className="mt-2 text-base text-muted-foreground leading-relaxed">
        Creating a workspace, adding the tag and trigger, and publishing the version. Usually under 30 seconds.
      </p>
      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-bg-tint p-4">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
        </span>
        <span className="text-sm text-muted-foreground">
          Account <span className="font-mono">{status.account_id}</span> · container <span className="font-mono">{status.container_id}</span>
        </span>
      </div>
    </>
  );
}

function SucceededView({
  status,
  websiteUrl,
  snippetVerified,
}: {
  status: GtmSetupStatus;
  websiteUrl: string | null;
  snippetVerified: boolean;
}) {
  const displayUrl =
    websiteUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '') ?? 'your site';

  // Snippet has fired → install is fully done; no need to nudge "open your site".
  if (snippetVerified) {
    return (
      <>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h3 className="text-xl font-bold text-dark">Setup complete</h3>
        </div>
        <p className="mt-2 text-base text-muted-foreground leading-relaxed">
          HeyMotek is installed in your Tag Manager and your snippet is live. Nothing else to do.
        </p>
        <div className="mt-5 rounded-xl bg-bg-tint p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Installed in
          </p>
          <p className="mt-1 text-sm font-mono font-semibold text-dark">
            Container {status.container_id} <span className="text-emerald-600">✓</span>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <h3 className="text-xl font-bold text-dark">HeyMotek is in your Tag Manager</h3>
      </div>
      <p className="mt-2 text-base text-muted-foreground leading-relaxed">
        Tag and trigger created, version published. Now visit your site once so the snippet fires — we'll detect it automatically and finish setup.
      </p>

      {websiteUrl && (
        <div className="mt-5">
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-dark text-white px-6 py-3 font-bold hover:bg-dark/90"
          >
            Open {displayUrl}
            <ExternalLink className="h-4 w-4" />
          </a>
          <p className="mt-2 text-sm text-muted-foreground">
            to fire the snippet and finish setup
          </p>
        </div>
      )}

      <div className="mt-5 rounded-xl bg-bg-tint p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Installed in
        </p>
        <p className="mt-1 text-sm font-mono font-semibold text-dark">
          Container {status.container_id} <span className="text-emerald-600">✓</span>
        </p>
      </div>
    </>
  );
}

function FailedView({ status }: { status: GtmSetupStatus }) {
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);

  async function handleRetry() {
    if (!status.account_id || !status.container_id) return;
    setRetrying(true);
    setRetryErr(null);
    try {
      await apiRequest('/oauth/gtm/install', {
        method: 'POST',
        body: { account_id: status.account_id, container_id: status.container_id },
      });
      await queryClient.invalidateQueries({ queryKey: ['gtm', 'setup-status'] });
    } catch (e: unknown) {
      setRetrying(false);
      setRetryErr(
        (e instanceof ApiError && e.detail) ||
          "Couldn't retry. Try reconnecting.",
      );
    }
  }

  async function handleReconnect() {
    setRetrying(true);
    setRetryErr(null);
    try {
      const { auth_url } = await apiRequest<StartResponse>('/oauth/gtm/start');
      window.location.href = auth_url;
    } catch (e: unknown) {
      setRetrying(false);
      setRetryErr(
        (e instanceof ApiError && e.detail) ||
          "Couldn't restart connection.",
      );
    }
  }

  return (
    <>
      <h3 className="text-xl font-bold text-dark">Install didn't finish</h3>
      <div className="mt-3">
        <Banner variant="error" size="compact">
          {status.error || "The install couldn't complete. Try again, or use the manual install below."}
        </Banner>
      </div>
      {retryErr && (
        <div className="mt-3">
          <Banner variant="error" size="compact">{retryErr}</Banner>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {status.account_id && status.container_id && (
          <Button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className="bg-dark text-white hover:bg-dark/90 rounded-full px-6 py-3 font-bold"
          >
            {retrying ? 'Retrying…' : 'Try again'}
          </Button>
        )}
        <button
          type="button"
          onClick={handleReconnect}
          disabled={retrying}
          className="text-sm text-muted-foreground underline underline-offset-2 hover:text-dark"
        >
          Reconnect Tag Manager
        </button>
      </div>
    </>
  );
}

function OAuthErrorView({ code }: { code: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isMissingScopes = code === 'missing_scopes';

  async function handleReconnect() {
    setBusy(true);
    setErr(null);
    try {
      const { auth_url } = await apiRequest<StartResponse>('/oauth/gtm/start');
      window.location.href = auth_url;
    } catch (e: unknown) {
      setBusy(false);
      setErr(
        (e instanceof ApiError && e.detail) ||
          "Couldn't restart connection.",
      );
    }
  }

  const heading = isMissingScopes
    ? 'Permissions missing'
    : "Connection didn't complete";
  const bannerText = isMissingScopes
    ? "We need all four Tag Manager permissions to install. Please reconnect and check all four boxes."
    : "We didn't get permission to install in your Tag Manager. Try again, or use the manual install below.";
  const ctaText = isMissingScopes ? 'Reconnect and grant all' : 'Try again';

  return (
    <>
      <h3 className="text-xl font-bold text-dark">{heading}</h3>
      <div className="mt-3">
        <Banner variant="error" size="compact">{bannerText}</Banner>
      </div>
      {err && (
        <div className="mt-3">
          <Banner variant="error" size="compact">{err}</Banner>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Reason code: <span className="font-mono">{code}</span>
      </p>
      <div className="mt-5">
        <Button
          type="button"
          onClick={handleReconnect}
          disabled={busy}
          className="bg-dark text-white hover:bg-dark/90 rounded-full px-6 py-3 font-bold"
        >
          {busy ? 'Opening Google…' : ctaText}
        </Button>
      </div>
    </>
  );
}
